import { useUnit } from 'effector-react/effector-react.umd';
import {
  $assessmentPage,
  $assessmentStatus,
  nextPage,
  resetAssessment,
  setAssessmentInfo,
  setAssessmentStatus,
  setQuestionIndex,
} from 'store/assessment/model';
import Card from 'components/Card';
import Instruction from 'pages/Assessment/screens/Instruction';
import { AssessmentPage, RefetchTrialInfo } from 'store/assessment/types';
import { useEffect } from 'react';
import SystemMessage from 'pages/Assessment/screens/SystemMessage';
import QuestionPage from 'pages/Assessment/screens/Question';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularLoading, useTelegram, useToast } from '@myhrbox/myhrbox-frontend-core';
import {
  Trial_Question_Status,
  Trial_Status,
  useSkipQuestionMutation,
  useTrialInfoQuery,
} from '@generated/graphql';
import ROUTES from 'constants/routes';
import useNavigateWithReplace from 'hooks/useNavigateReplace';
import { resetQuestionTimer } from 'store/assessment/questionTimer';
import AssesmentAccessCard from 'components/AssesmentAccessCard';
import { TRIAL_IS_NOT_AVAILABLE_ERROR_MESSAGE } from 'constants/assessment';

const CurrentPage = ({
  currentPage,
  refetchTrialInfo,
  remainingTime,
}: {
  currentPage: AssessmentPage;
  refetchTrialInfo: RefetchTrialInfo;
  remainingTime: number | null;
}) => {
  if (currentPage === AssessmentPage.Instructions) return <Instruction />;
  if (currentPage === AssessmentPage.Preview)
    return <SystemMessage remainingTime={remainingTime} />;
  if (currentPage === AssessmentPage.Question)
    return <QuestionPage refetchTrialInfo={refetchTrialInfo} remainingTime={remainingTime} />;
  return null;
};

const Assessment = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { showErrorToast } = useToast();
  const navigate = useNavigate();
  const { isTelegram, tg } = useTelegram();
  const { showPreventNavigateModal } = useNavigateWithReplace();

  const [currentPage, assessmentStatus] = useUnit([$assessmentPage, $assessmentStatus]);
  const shouldBlockReload = assessmentStatus === Trial_Status.Started;

  const {
    data: trialInfoData,
    loading: trialInfoLoading,
    refetch: refetchTrialInfo,
    error: trialInfoError,
  } = useTrialInfoQuery({
    variables: {
      trialId: id!,
    },
    fetchPolicy: 'network-only',
  });
  // @ts-expect-error типизация неправильная
  const trialErrorMessage = trialInfoError?.cause?.extensions.originalError.error;
  const trialInfo = trialInfoData?.trialInfo;
  const questions = trialInfo?.questionList;

  const [skipQuestion] = useSkipQuestionMutation();

  useEffect(() => {
    if (!trialInfoLoading) {
      if (trialInfo) {
        if (questions?.length) {
          if ([Trial_Status.Created, Trial_Status.Received].includes(trialInfo.status)) {
            setAssessmentInfo(trialInfo);
            setAssessmentStatus(Trial_Status.Received);
          } else {
            const activeQuestionIndex = questions.findIndex((question) =>
              [Trial_Question_Status.Pending, Trial_Question_Status.Delayed].includes(
                question.status,
              ),
            );

            if (activeQuestionIndex !== -1) {
              setQuestionIndex(activeQuestionIndex);
              nextPage();
              setAssessmentInfo(trialInfo);
              setAssessmentStatus(Trial_Status.Received);
            } else {
              const startedQuestion = questions.find(
                (question) => question.status === Trial_Question_Status.Started,
              );

              if (startedQuestion) {
                skipQuestion({
                  variables: {
                    input: {
                      questionId: startedQuestion.questionId,
                      trialId: trialInfo!.id,
                    },
                  },
                }).finally(() => {
                  showErrorToast(t('assessment.instruction.error.assessmentUnavailable'));
                  navigate(ROUTES.ASSESSMENT, { replace: true });
                });
              }
            }
          }
        } else {
          showErrorToast(t('assessment.instruction.error.getQuestionList'));
          navigate(ROUTES.ASSESSMENT, { replace: true });
        }
      } else if (trialErrorMessage !== TRIAL_IS_NOT_AVAILABLE_ERROR_MESSAGE) {
        navigate(ROUTES.ASSESSMENT, { replace: true });
        showErrorToast(t('assessment.instruction.error.assessmentUnavailable'));
      }
    }
  }, [trialInfo, trialInfoLoading, trialErrorMessage]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      currentLocation.pathname !== nextLocation.pathname &&
      nextLocation.state === null &&
      currentPage === AssessmentPage.Question
    );
  });

  const handleResetAssesment = () => {
    resetQuestionTimer();
    resetAssessment();
  };

  useEffect(() => {
    if (blocker.state === 'blocked') {
      showPreventNavigateModal({
        onConfirm: async () => {
          handleResetAssesment();
          blocker.proceed();
        },
        onCancel: () => blocker.reset(),
      });
    }
  }, [blocker]);

  useEffect(() => {
    if (isTelegram) {
      tg.enableClosingConfirmation();
    }
    return () => tg.disableClosingConfirmation();
  }, []);

  useEffect(() => {
    if (!shouldBlockReload) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockReload]);

  useEffect(() => {
    return () => handleResetAssesment();
  }, []);

  const remainingTime = questions
    ? questions.reduce((questionsAcc, question) => {
        if (
          [Trial_Question_Status.Started, Trial_Question_Status.Skipped].includes(question.status)
        ) {
          return questionsAcc - question.time;
        }
        return questionsAcc - (question.candidateTime || 0);
      }, trialInfo?.totalTime || 0)
    : null;

  if ((trialInfoLoading || !assessmentStatus) && !trialErrorMessage) return <CircularLoading />;

  if (trialErrorMessage === TRIAL_IS_NOT_AVAILABLE_ERROR_MESSAGE) return <AssesmentAccessCard />;

  return (
    <Card sx={{ maxWidth: 976 }}>
      <CurrentPage
        currentPage={currentPage}
        refetchTrialInfo={refetchTrialInfo}
        remainingTime={remainingTime}
      />
    </Card>
  );
};

export default Assessment;
